import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Headphones,
  Music2,
  Image as ImageIcon,
  Video as VideoIcon,
  Heart,
  MessageCircle,
  ExternalLink,
  X
} from 'lucide-react';
import contentService from '../services/contentService';
import logger from '../utils/logging';
import analyticsService from '../services/analyticsService';
import { productDetailPathFromName } from '../utils/productPath';
import socialEngagementService from '../services/socialEngagementService';
import { useAuth } from '../context/AuthContext';

const NAV_ICON_SRC = '/favicon.png';

const handleImageFallback = (event) => {
  if (!event?.currentTarget) return;
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === 'true') return;
  img.dataset.fallbackApplied = 'true';
  img.src = NAV_ICON_SRC;
  img.style.objectFit = 'contain';
  img.style.background = 'rgba(15, 23, 42, 0.8)';
  img.style.padding = '10px';
};

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${url}`;
};

const resolveEntrySource = (folder, url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return resolveUrl(url);
  const folderPath = folder ? `/content/${folder}/` : '/content/';
  return resolveUrl(`${folderPath}${url}`);
};

const extractMusicLinks = (entry) => {
  if (!entry) return [];
  const rawContent = entry;
  const linkBuckets = [
    rawContent.musicLinks,
    rawContent.links,
    rawContent.platformLinks,
    rawContent.streamingLinks,
    rawContent.socialLinks,
    rawContent.musicPlatforms,
    rawContent.platforms,
    rawContent.social,
    rawContent.externalLinks
  ];
  const links = [];

  const streamingKeywords = ['spotify', 'apple music', 'itunes', 'youtube music', 'youtube', 'amazon music', 'deezer', 'tidal', 'soundcloud', 'bandcamp', 'pandora'];
  const streamingHosts = ['spotify.com', 'music.apple.com', 'itunes.apple.com', 'music.youtube.com', 'youtube.com', 'music.amazon.', 'deezer.com', 'tidal.com', 'soundcloud.com', 'bandcamp.com', 'pandora.com'];

  const isStreamingLink = (label = '', url = '') => {
    const lowerLabel = label.toLowerCase();
    const lowerUrl = url.toLowerCase();
    if (streamingKeywords.some((keyword) => lowerLabel.includes(keyword) || lowerUrl.includes(keyword))) {
      return true;
    }
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      return streamingHosts.some((host) => hostname.includes(host));
    } catch (err) {
      return false;
    }
  };

  linkBuckets.forEach((bucket) => {
    if (!bucket) return;
    if (Array.isArray(bucket)) {
      bucket.forEach((entry) => {
        if (!entry) return;
        if (typeof entry === 'string') {
          if (isStreamingLink(entry, entry)) {
            links.push({ label: entry, url: entry });
          }
        } else if (typeof entry === 'object') {
          const label = entry.label || entry.name || entry.platform || entry.title;
          const url = entry.url || entry.href;
          if (label && url && isStreamingLink(label, url)) {
            links.push({ label, url });
          }
        }
      });
    } else if (typeof bucket === 'object') {
      Object.entries(bucket).forEach(([key, value]) => {
        if (!value) return;
        if (typeof value === 'string') {
          if (isStreamingLink(key, value)) {
            links.push({ label: key, url: value });
          }
        } else if (value.url && isStreamingLink(key, value.url)) {
          links.push({ label: key, url: value.url });
        }
      });
    }
  });

  Object.entries(rawContent).forEach(([key, value]) => {
    if (typeof value === 'string' && /^https?:\/\//i.test(value) && isStreamingLink(key, value)) {
      links.push({ label: key, url: value });
    }
  });

  const seen = new Set();
  return links.filter(({ url }) => {
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
};

const extractPostedDestinations = (entry) => {
  if (!entry) return [];
  const destinations = [];
  const seen = new Set();
  const add = (platform, url) => {
    if (!platform) return;
    const key = `${platform}:${url || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    destinations.push({ platform, url: url || null });
  };

  if (entry.isInZFeed) add('website', 'https://rainbow-sails.shop/social');
  if (entry.tags && typeof entry.tags === 'object' && entry.tags.subscriptionOnly) {
    add('subscription', null);
  }

  if (Array.isArray(entry.socialPosts)) {
    entry.socialPosts.forEach((post) => {
      if (!post || !post.platform) return;
      add(String(post.platform), post.platformUrl || null);
    });
  }

  return destinations;
};

const dedupe = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item || seen.has(item)) return false;
    seen.add(item);
    return true;
  });
};

const detectContentType = (item) => {
  if (!item) return '';
  if (item.contentType) return item.contentType;
  const raw = item.rawContent || {};
  // Prioritize actual content type from data over inferred types
  let type = raw.contentType || raw.type || raw.category;

  // Only infer from tags if no explicit type is set
  if (!type && Array.isArray(raw.tags)) {
    const musicTag = raw.tags.find((tag) => typeof tag === 'string' && tag.toLowerCase().includes('music'));
    if (musicTag) {
      type = 'music';
    }
  }

  // Only infer from audio if no explicit type is set (audio takes precedence over default image)
  if (!type && item.audio?.length) {
    type = 'music';
  }

  // Don't infer from videos - videos are only for YouTube, not for site display
  // The content type should come from the data itself

  if (!type) {
    type = item.primaryMediaType || 'image';
  }

  return type;
};

const formatFilterLabel = (value) => {
  if (!value || value === 'all') return 'All';
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildFeedItems = (content = []) => {
  return content
    .map((entry) => {
      if (!entry) return null;

      const folder = entry.folder || entry.id || '';
      const title = entry.name || 'Zee Creation';
      const description = entry.description || 'Zee is crafting new dreams for you.';

      const explicitImages = Array.isArray(entry.images) ? entry.images : [];
      const explicitVideos = Array.isArray(entry.videos) ? entry.videos : [];
      const explicitAudio = Array.isArray(entry.audio) ? entry.audio : Array.isArray(entry.audios) ? entry.audios : [];

      const mediaImages = [];
      const mediaVideos = [];
      const mediaAudio = [];

      if (Array.isArray(entry.media)) {
        entry.media.forEach((mediaItem) => {
          if (!mediaItem || !mediaItem.url) return;
          const resolved = resolveEntrySource(folder, mediaItem.url);
          if (!resolved) return;
          const type = (mediaItem.type || '').toLowerCase();
          if (type === 'video' || mediaItem.isVideo || /\.(mp4|mov|webm)$/i.test(mediaItem.name || '')) {
            mediaVideos.push(resolved);
          } else if (type === 'audio' || /\.(mp3|wav|aac|m4a)$/i.test(mediaItem.name || '')) {
            mediaAudio.push(resolved);
          } else {
            mediaImages.push(resolved);
          }
        });
      }

      const images = dedupe([
        entry.coverImage ? resolveUrl(entry.coverImage) : null,
        entry.coverMedia?.url ? resolveEntrySource(folder, entry.coverMedia.url) : null,
        ...explicitImages.map((img) => resolveEntrySource(folder, img)),
        ...mediaImages
      ]);

      const detectedVideos = dedupe([
        ...explicitVideos.map((vid) => resolveEntrySource(folder, vid)),
        ...mediaVideos
      ]);

      // Check for audioUrl in tags (backend stores audio files here)
      const tagsAudio = [];
      if (entry.tags) {
        try {
          const tags = typeof entry.tags === 'string' ? JSON.parse(entry.tags) : entry.tags;
          if (tags && typeof tags === 'object' && tags.audioUrl) {
            // Resolve the audio URL properly
            const audioUrl = tags.audioUrl.startsWith('http') 
              ? tags.audioUrl 
              : resolveUrl(tags.audioUrl);
            tagsAudio.push(audioUrl);
          }
        } catch (e) {
          // Tags not valid JSON, skip
        }
      }

      const detectedAudio = dedupe([
        ...tagsAudio, // Add audio from tags first (most reliable)
        ...explicitAudio.map((aud) => resolveEntrySource(folder, aud)),
        ...mediaAudio
      ]);

      const streamingLinks = extractMusicLinks(entry);

      // Get content type from actual data, prioritizing explicit content type
      const rawContentType = entry.contentType || entry.type || entry.category;
      let contentType = rawContentType ? rawContentType.toString().trim().toLowerCase() : '';
      
      // Only infer from audio if no explicit type is set (audio takes precedence)
      if (!contentType && detectedAudio.length) {
        contentType = 'music';
      }
      // Don't infer from videos - videos are only for YouTube, not for site display
      // If no explicit type, default to image
      if (!contentType) {
        contentType = 'image';
      }

      const isImageContent = contentType.includes('image');
      const isMusicContent = contentType.includes('music') || contentType.includes('audio');
      
      // For music content, prioritize MP4 videos for display on the feed
      // Filter for MP4 videos specifically (check before query parameters)
      const mp4Videos = detectedVideos.filter((vid) => {
        const urlWithoutQuery = vid.split('?')[0].toLowerCase();
        return urlWithoutQuery.endsWith('.mp4') || urlWithoutQuery.includes('.mp4');
      });
      
      // Only use videos if content type explicitly indicates video (not just because videos exist)
      // OR if it's music content with MP4 videos
      const videos = (contentType.includes('video') && !isImageContent) 
        ? detectedVideos 
        : (isMusicContent && mp4Videos.length > 0) 
          ? mp4Videos 
          : [];
      const audio = detectedAudio;

      let primaryMediaType = 'image';
      let primaryMediaUrl = images[0] || null;

      // For music content with MP4 videos, display the video on the feed
      if (isMusicContent && mp4Videos.length > 0) {
        primaryMediaType = 'video';
        primaryMediaUrl = mp4Videos[0];
      } else if (contentType.includes('video') && !isImageContent && videos.length) {
        primaryMediaType = 'video';
        primaryMediaUrl = videos[0];
      } else if (isMusicContent) {
        primaryMediaType = 'audio';
        primaryMediaUrl = audio[0] || images[0] || null;
      } else {
        // Default to image - always show images even if videos exist
        primaryMediaType = 'image';
        primaryMediaUrl = images[0] || null;
      }

      const productLinkMap = new Map();
      const addProductLink = (label, url) => {
        if (!url) return;
        const finalLabel = label || 'View Product';
        if (!productLinkMap.has(url)) {
          productLinkMap.set(url, finalLabel);
        }
      };

      if (entry.productUrl) {
        addProductLink(entry.productName, entry.productUrl);
      }
      if (entry.productId && entry.productName) {
        addProductLink(`Buy ${entry.productName}`, productDetailPathFromName(entry.productName));
      }
      if (Array.isArray(entry.productLinks)) {
        entry.productLinks.forEach((link) => {
          if (!link) return;
          if (typeof link === 'string') {
            addProductLink(link, link);
          } else if (link.url) {
            addProductLink(link.label || link.name, link.url);
          }
        });
      }
      if (Array.isArray(entry.products)) {
        entry.products.forEach((product) => {
          if (!product) return;
          if (product.url) {
            addProductLink(product.label || product.name, product.url);
          } else if (product.productName) {
            addProductLink(`Buy ${product.productName}`, productDetailPathFromName(product.productName));
          }
        });
      }

      const productLinks = Array.from(productLinkMap.entries()).map(([url, label]) => ({ label, url }));
      const postedDestinations = extractPostedDestinations(entry);

      return {
        id: entry.id || folder || `${title}-${Math.random()}`,
        contentId: folder,
        title,
        description,
        contentType,
        coverImage: images[0] || null,
        primaryMediaType,
        primaryMediaUrl,
        images,
        videos,
        audio,
        streamingLinks,
        productLinks,
        postedDestinations,
        rawContent: entry
      };
    })
    .filter(Boolean);
};

const parseDateValue = (value) => {
  if (!value) return 0;

  if (typeof value === 'number') {
    if (value > 1e12) {
      return value;
    }
    if (value > 1e9) {
      return value * 1000;
    }
    return value;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return parseDateValue(numeric);
    }

    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const extractTimestampFromIdentifier = (value) => {
  if (!value) return 0;
  const text = String(value);

  // Match a 13+ digit millisecond timestamp or 10+ digit second timestamp at the start
  const prefixMatch = text.match(/^(\d{10,})/);
  if (prefixMatch) {
    const numeric = Number(prefixMatch[1]);
    if (!Number.isNaN(numeric)) {
      return numeric > 1e12 ? numeric : numeric * 1000;
    }
  }

  // Match any large number within the string (e.g., "1234567890_folder-name")
  const embeddedMatch = text.match(/(\d{10,})/);
  if (embeddedMatch) {
    const numeric = Number(embeddedMatch[1]);
    if (!Number.isNaN(numeric)) {
      return numeric > 1e12 ? numeric : numeric * 1000;
    }
  }

  return 0;
};

const getFeedItemTimestamp = (item) => {
  if (!item) return 0;

  const sources = [];
  const raw = item.rawContent || item;
  if (raw) {
    sources.push(raw);
  }
  if (raw?.metadata) {
    sources.push(raw.metadata);
  }
  if (raw?.meta) {
    sources.push(raw.meta);
  }
  if (raw?.details) {
    sources.push(raw.details);
  }
  if (raw?.attributes) {
    sources.push(raw.attributes);
  }

  const fields = [
    'publishedAt',
    'published_at',
    'releaseDate',
    'release_date',
    'date',
    'createdAt',
    'created_at',
    'updatedAt',
    'updated_at',
    'timestamp',
    'lastModified',
    'last_modified',
    'sortDate',
    'sort_date'
  ];

  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;

    for (const field of fields) {
      if (field in source) {
        const parsed = parseDateValue(source[field]);
        if (parsed) {
          return parsed;
        }
      }
    }
  }

  const ordinalCandidates = [
    raw?.folder,
    raw?.id,
    item.contentId,
    item.id,
    item.title
  ];

  for (const candidate of ordinalCandidates) {
    const extracted = extractTimestampFromIdentifier(candidate);
    if (extracted) {
      return extracted;
    }
  }

  if (raw?.createdAt || raw?.updatedAt || raw?.date) {
    const parsed = parseDateValue(raw.createdAt || raw.updatedAt || raw.date);
    if (parsed) return parsed;
  }

  return 0;
};

const LOCAL_STORAGE_LIKES_KEY = 'zee_social_likes';
const LOCAL_STORAGE_NAME_KEY = 'zee_social_display_name';

const getDefaultEngagement = () => ({
  likes: 0,
  likedBy: [],
  comments: []
});

const SocialFeed = () => {
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioStates, setAudioStates] = useState({});
  const [videoStates, setVideoStates] = useState({});
  const [videoMetadata, setVideoMetadata] = useState({});
  const videoStatesRef = useRef({});
  const [engagementMap, setEngagementMap] = useState({});
  const [likedContent, setLikedContent] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_LIKES_KEY);
      if (!stored) return {};
      const ids = JSON.parse(stored);
      return ids.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
    } catch (err) {
      console.warn('Failed to parse stored likes', err);
      return {};
    }
  });
  const [defaultDisplayName, setDefaultDisplayName] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [commentForm, setCommentForm] = useState({ name: '', message: '' });
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debugSnapshot, setDebugSnapshot] = useState(null);
  const modalVideoRef = useRef(null);
  const modalAudioRef = useRef(null);
  const sectionsRef = useRef([]);
  const observerRef = useRef(null);
  const loadedItemsRef = useRef(new Set()); // Track which items have been loaded
  const loadingItemRef = useRef(null); // Track which item is currently loading
  const { user } = useAuth();
  const navigate = useNavigate();
  const emojiOptions = useMemo(() => ['🌈', '🔥', '💫', '😍', '🎶', '✨', '🌀', '❤️', '⚡'], []);
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filterOptions = useMemo(() => {
    const set = new Set(['all']);
    feedItems.forEach((item) => {
      const type = detectContentType(item)?.toLowerCase?.() || '';
      if (type) {
        set.add(type);
      }
    });
    return Array.from(set);
  }, [feedItems]);

  useEffect(() => {
    if (!filterOptions.includes(activeFilter)) {
      setActiveFilter('all');
    }
  }, [filterOptions, activeFilter]);

  useEffect(() => {
    const loadFeed = async () => {
      try {
        setLoading(true);
        logger.log('info', 'system', 'Loading Zee social feed', { timestamp: Date.now() });
        const content = await contentService.getContent();
        analyticsService.trackEvent('social_feed_load', {
          count: Array.isArray(content) ? content.length : 0,
        });
        const items = buildFeedItems(content);
        const sortedItems = [...items].sort((a, b) => {
          const timeDiff = getFeedItemTimestamp(b) - getFeedItemTimestamp(a);
          if (timeDiff !== 0) {
            return timeDiff;
          }

          const aName = (a.rawContent?.name || a.title || '').toString().toLowerCase();
          const bName = (b.rawContent?.name || b.title || '').toString().toLowerCase();
          if (aName && bName) {
            return bName.localeCompare(aName);
          }
          return 0;
        });
        setFeedItems(sortedItems);
        const contentDebug = typeof window !== 'undefined' && Array.isArray(window.__CONTENT_DEBUG)
          ? window.__CONTENT_DEBUG.slice(-8)
          : [];
        setDebugSnapshot({
          fetchedCount: Array.isArray(content) ? content.length : 0,
          builtCount: items.length,
          sortedCount: sortedItems.length,
          firstItem: sortedItems[0]
            ? {
                title: sortedItems[0].title,
                type: sortedItems[0].contentType,
                mediaType: sortedItems[0].primaryMediaType,
                mediaUrl: sortedItems[0].primaryMediaUrl,
              }
            : null,
          contentDebug,
        });
        setError('');
      } catch (err) {
        logger.logError(err, 'SocialFeed loadFeed');
        setError('Zee is patching the stream. Please refresh to keep vibing.');
      } finally {
        setLoading(false);
      }
    };

    loadFeed();
  }, []);

  // Sequential loading function - loads one item at a time
  const loadItemSequentially = useCallback((index) => {
    // Don't load if already loaded or currently loading
    if (loadedItemsRef.current.has(index) || loadingItemRef.current === index) {
      return;
    }

    // Don't load if previous item isn't loaded yet
    if (index > 0 && !loadedItemsRef.current.has(index - 1)) {
      return;
    }

    const section = sectionsRef.current[index];
    if (!section) return;

    loadingItemRef.current = index;

    const videoEl = section.querySelector('[data-feed-video="true"]');
    const audioEl = section.querySelector('audio');
    const item = feedItems[index];

    let loadedCount = 0;
    let totalToLoad = 0;

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount >= totalToLoad) {
        loadedItemsRef.current.add(index);
        loadingItemRef.current = null;
        
        // Load next item if it exists
        if (index + 1 < feedItems.length) {
          setTimeout(() => {
            loadItemSequentially(index + 1);
          }, 100);
        }
      }
    };

    // Load video if exists
    if (videoEl && item.primaryMediaType === 'video') {
      totalToLoad++;
      try {
        videoEl.preload = 'auto';
        if (videoEl.readyState < 2) {
          const handleCanPlay = () => {
            checkComplete();
            videoEl.removeEventListener('canplay', handleCanPlay);
            videoEl.removeEventListener('error', handleError);
          };
          const handleError = () => {
            checkComplete(); // Still count as loaded even if error
            videoEl.removeEventListener('canplay', handleCanPlay);
            videoEl.removeEventListener('error', handleError);
          };
          videoEl.addEventListener('canplay', handleCanPlay, { once: true });
          videoEl.addEventListener('error', handleError, { once: true });
          videoEl.load();
        } else {
          checkComplete();
        }
      } catch (err) {
        logger.logError(err, 'SocialFeed sequential video load');
        checkComplete();
      }
    }

    // Load audio if exists
    if (audioEl && item.primaryMediaType === 'audio') {
      totalToLoad++;
      try {
        audioEl.preload = 'auto';
        if (audioEl.readyState < 2) {
          const handleCanPlay = () => {
            checkComplete();
            audioEl.removeEventListener('canplay', handleCanPlay);
            audioEl.removeEventListener('error', handleError);
          };
          const handleError = () => {
            checkComplete(); // Still count as loaded even if error
            audioEl.removeEventListener('canplay', handleCanPlay);
            audioEl.removeEventListener('error', handleError);
          };
          audioEl.addEventListener('canplay', handleCanPlay, { once: true });
          audioEl.addEventListener('error', handleError, { once: true });
          audioEl.load();
        } else {
          checkComplete();
        }
      } catch (err) {
        logger.logError(err, 'SocialFeed sequential audio load');
        checkComplete();
      }
    }

    // For image-only content, mark as loaded immediately
    if (totalToLoad === 0) {
      loadedItemsRef.current.add(index);
      loadingItemRef.current = null;
      
      // Load next item if it exists
      if (index + 1 < feedItems.length) {
        setTimeout(() => {
          loadItemSequentially(index + 1);
        }, 100);
      }
    }
  }, [feedItems]);

  // Start loading first item when feed loads
  useEffect(() => {
    if (!feedItems.length) return;

    // Reset loaded items when feed changes
    loadedItemsRef.current.clear();
    loadingItemRef.current = null;

    // Wait for DOM to be ready, then start loading first item
    const timer = setTimeout(() => {
      if (feedItems.length > 0) {
        loadItemSequentially(0);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [feedItems, loadItemSequentially]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedName = window.localStorage.getItem(LOCAL_STORAGE_NAME_KEY);
      if (storedName) {
        setDefaultDisplayName(storedName);
      }
    }
  }, []);

  useEffect(() => {
    videoStatesRef.current = videoStates;
  }, [videoStates]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user) {
      window.localStorage.removeItem(LOCAL_STORAGE_LIKES_KEY);
      return;
    }
    const likedIds = Object.keys(likedContent).filter((id) => likedContent[id]);
    window.localStorage.setItem(LOCAL_STORAGE_LIKES_KEY, JSON.stringify(likedIds));
  }, [likedContent, user]);

  useEffect(() => {
    if (!user) {
      setLikedContent({});
    }
  }, [user]);

  const registerSection = useCallback((element, index) => {
    const previous = sectionsRef.current[index];
    if (previous && observerRef.current) {
      observerRef.current.unobserve(previous);
    }

    sectionsRef.current[index] = element;

    if (element && observerRef.current) {
      observerRef.current.observe(element);
    }
  }, []);

  const pauseAudioAtIndex = useCallback((index) => {
    const section = sectionsRef.current[index];
    if (!section) return;
    const audioEl = section.querySelector('audio');
    if (!audioEl) return;

    audioEl.pause();
    audioEl.currentTime = 0;
    setAudioStates((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const updateVideoState = useCallback((index, updater) => {
    setVideoStates((prev) => {
      const previous = prev[index] || { playing: false, userPaused: false };
      const resolved =
        typeof updater === 'function' ? updater(previous) : { ...previous, ...updater };

      if (!resolved) {
        if (!prev[index]) {
          return prev;
        }
        const next = { ...prev };
        delete next[index];
        return next;
      }

      if (
        previous.playing === resolved.playing &&
        previous.userPaused === resolved.userPaused
      ) {
        return prev;
      }

      return {
        ...prev,
        [index]: resolved
      };
    });
  }, []);


  const pauseAllFeedVideos = useCallback(() => {
    sectionsRef.current.forEach((section, index) => {
      if (!section) return;
      const videoEl = section.querySelector('[data-feed-video="true"]');
      if (videoEl && !videoEl.paused) {
        videoEl.pause();
        // Don't reset currentTime - keep position
        // Don't set userPaused: true - this is a system pause, not user-initiated
        updateVideoState(index, { playing: false });
      }
    });
  }, [updateVideoState]);

  const playAudioAtIndex = useCallback(
    (index) => {
      const section = sectionsRef.current[index];
      if (!section) return;
      const audioEl = section.querySelector('audio');
      if (!audioEl) return;

      sectionsRef.current.forEach((node, idx) => {
        if (idx !== index && node) {
          const otherAudio = node.querySelector('audio');
          if (otherAudio && !otherAudio.paused) {
            otherAudio.pause();
            otherAudio.currentTime = 0;
          }
        }
      });

      try {
        if (audioEl.readyState < 2) {
          audioEl.load();
        }
      } catch (loadError) {
        logger.logError(loadError, 'SocialFeed audio load');
      }

      audioEl.muted = false;
      const playPromise = audioEl.play();

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            setAudioStates({ [index]: true });
            analyticsService.trackEvent('social_feed_audio_autoplay', {
              contentId: feedItems[index]?.rawContent?.id,
            });
          })
          .catch((err) => {
            logger.logError(err, 'SocialFeed audio autoplay blocked - retry muted');
            audioEl.muted = true;
            const retryPromise = audioEl.play();
            if (retryPromise && typeof retryPromise.then === 'function') {
              retryPromise
                .then(() => {
                  setAudioStates({ [index]: true });
                })
                .catch((retryErr) => {
                  logger.logError(retryErr, 'SocialFeed audio retry failed');
                });
            }
          });
      }
    },
    [feedItems]
  );

  const handleVideoMetadata = useCallback((index, event) => {
    const { videoWidth, videoHeight } = event.target || {};
    if (!videoWidth || !videoHeight) {
      return;
    }

    setVideoMetadata((prev) => {
      const previous = prev[index];
      if (previous && previous.width === videoWidth && previous.height === videoHeight) {
        return prev;
      }

      return {
        ...prev,
        [index]: { width: videoWidth, height: videoHeight }
      };
    });
  }, []);

  const handleVideoToggle = useCallback(
    (index) => {
      const section = sectionsRef.current[index];
      if (!section) return;
      const videoEl = section.querySelector('[data-feed-video="true"]');
      if (!videoEl) return;

      if (videoEl.paused || videoEl.ended) {
        videoEl.defaultMuted = false;
        videoEl.muted = false;
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              updateVideoState(index, { playing: true, userPaused: false });
              analyticsService.trackEvent('social_feed_video_play', {
                contentId: feedItems[index]?.rawContent?.id,
              });
            })
            .catch((err) => {
              logger.logError(err, 'SocialFeed manual video play');
            });
        } else {
          updateVideoState(index, { playing: true, userPaused: false });
          analyticsService.trackEvent('social_feed_video_play', {
            contentId: feedItems[index]?.rawContent?.id,
          });
        }
      } else {
        videoEl.pause();
        updateVideoState(index, { playing: false, userPaused: true });
        analyticsService.trackEvent('social_feed_video_pause', {
          contentId: feedItems[index]?.rawContent?.id,
        });
      }
      
      // Update state immediately for UI responsiveness
      const currentState = videoStatesRef.current[index] || {};
      videoStatesRef.current[index] = {
        ...currentState,
        playing: !videoEl.paused,
        userPaused: videoEl.paused
      };
    },
    [feedItems, updateVideoState]
  );

  useEffect(() => {
    if (!feedItems.length) {
      return () => {};
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Sequential loading is handled separately, so we don't need a preload observer

    // Custom scroll handler for desktop browser autoplay logic
    // When viewport top enters bottom 10% of a card, start playing the next card
    const handleScrollAutoplay = () => {
      // Only on desktop browser (not mobile)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) return;
      
      if (activeModal) return;
      
      const viewportHeight = window.innerHeight;
      
      sectionsRef.current.forEach((section, index) => {
        if (!section) return;
        
        const rect = section.getBoundingClientRect();
        
        // rect.top is distance from viewport top to card top (can be negative)
        // rect.bottom is distance from viewport top to card bottom
        // Bottom 10% of card starts at: rect.bottom - (rect.height * 0.1)
        // We want to trigger when viewport top (which is at position 0) reaches this point
        // So we check if rect.bottom - (rect.height * 0.1) <= 0 (viewport top has passed it)
        const bottom10PercentThreshold = rect.bottom - (rect.height * 0.1);
        const isInBottom10Percent = bottom10PercentThreshold <= 0 && rect.bottom > 0;
        
        // Check if this card is currently in view
        const isInView = rect.top < viewportHeight && rect.bottom > 0;
        
        // If viewport top has entered bottom 10% of this card, pause this one and play the NEXT card
        if (isInBottom10Percent && index < feedItems.length - 1) {
          // Pause current card
          const videoEl = section.querySelector('[data-feed-video="true"]');
          const audioEl = section.querySelector('audio');
          const videoState = videoStatesRef.current[index] || {};
          if (videoEl && !videoEl.paused && !videoState.userPaused) {
            videoEl.pause();
            updateVideoState(index, (prev) => ({ ...prev, playing: false }));
          }
          if (audioEl && !audioEl.paused) {
            audioEl.pause();
            setAudioStates((prev) => {
              if (!prev[index]) return prev;
              const next = { ...prev };
              delete next[index];
              return next;
            });
          }
          
          // Play next card
          const nextIndex = index + 1;
          const nextSection = sectionsRef.current[nextIndex];
          if (!nextSection) return;
          
          const nextVideoEl = nextSection.querySelector('[data-feed-video="true"]');
          const nextAudioEl = nextSection.querySelector('audio');
          const nextItem = feedItems[nextIndex];
          const nextVideoState = videoStatesRef.current[nextIndex] || {};
          
          // Play next video - only if it's been loaded
          if (nextVideoEl && nextVideoEl.paused && !nextVideoState.userPaused && loadedItemsRef.current.has(nextIndex)) {
            nextVideoEl.muted = false;
            nextVideoEl.setAttribute('playsinline', 'true');
            
            // Only play if already loaded
            if (nextVideoEl.readyState >= 2) {
              nextVideoEl.play()
                .then(() => {
                  updateVideoState(nextIndex, { playing: true, userPaused: false });
                })
                .catch((err) => {
                  logger.logError(err, 'SocialFeed next video autoplay blocked');
                });
            } else {
              // Wait for it to load
              const playVideo = () => {
                nextVideoEl.play()
                  .then(() => {
                    updateVideoState(nextIndex, { playing: true, userPaused: false });
                  })
                  .catch((err) => {
                    logger.logError(err, 'SocialFeed next video autoplay blocked');
                  });
              };
              nextVideoEl.addEventListener('loadeddata', playVideo, { once: true });
            }
          }
          
          // Play next audio - only if it's been loaded
          if (nextItem?.primaryMediaType === 'audio' && nextAudioEl && nextAudioEl.paused && loadedItemsRef.current.has(nextIndex)) {
            playAudioAtIndex(nextIndex);
          }
        }
        
        // Also play first card when it comes into view initially - only if loaded
        if (index === 0 && isInView && loadedItemsRef.current.has(index)) {
          const videoEl = section.querySelector('[data-feed-video="true"]');
          const audioEl = section.querySelector('audio');
          const item = feedItems[index];
          const videoState = videoStatesRef.current[index] || {};
          
          if (videoEl && videoEl.paused && !videoState.userPaused) {
            videoEl.muted = false;
            videoEl.setAttribute('playsinline', 'true');
            
            // Only play if already loaded
            if (videoEl.readyState >= 2) {
              videoEl.play()
                .then(() => {
                  updateVideoState(index, { playing: true, userPaused: false });
                })
                .catch((err) => {
                  logger.logError(err, 'SocialFeed first video autoplay blocked');
                });
            } else {
              // Wait for it to load
              const playVideo = () => {
                videoEl.play()
                  .then(() => {
                    updateVideoState(index, { playing: true, userPaused: false });
                  })
                  .catch((err) => {
                    logger.logError(err, 'SocialFeed first video autoplay blocked');
                  });
              };
              videoEl.addEventListener('loadeddata', playVideo, { once: true });
            }
          }
          
          if (item?.primaryMediaType === 'audio' && audioEl && audioEl.paused) {
            playAudioAtIndex(index);
          }
        }
        
        // Pause current card if it's out of view
        if (!isInView) {
          const videoEl = section.querySelector('[data-feed-video="true"]');
          const audioEl = section.querySelector('audio');
          const videoState = videoStatesRef.current[index] || {};
          
          if (videoEl && !videoEl.paused && !videoState.userPaused) {
            videoEl.pause();
            updateVideoState(index, (prev) => ({ ...prev, playing: false }));
          }
          
          if (audioEl && !audioEl.paused) {
            audioEl.pause();
            setAudioStates((prev) => {
              if (!prev[index]) return prev;
              const next = { ...prev };
              delete next[index];
              return next;
            });
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const { target, isIntersecting, intersectionRatio } = entry;
          
          if (isIntersecting && intersectionRatio >= 0.5) {
            target.classList.add('active');
          } else if (!isIntersecting || intersectionRatio < 0.3) {
            target.classList.remove('active');
          }
        });
        
        // Trigger custom autoplay logic after intersection check
        handleScrollAutoplay();
      },
      { threshold: [0, 0.3, 0.5, 0.7, 1.0] }
    );
    
    // Also listen to scroll events for more responsive autoplay
    let scrollTimeout;
    const scrollHandler = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        handleScrollAutoplay();
      }, 50);
    };
    
    window.addEventListener('scroll', scrollHandler, { passive: true });

    sectionsRef.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    // Initial check for videos already in view on page load
    const checkInitialVisibleVideos = () => {
      const entries = observerRef.current.takeRecords();
      
      entries.forEach((entry) => {
        const { target, isIntersecting, intersectionRatio } = entry;
        const index = Number(target.dataset.index);
        const item = feedItems[index];
        
        if (isIntersecting && intersectionRatio >= 0.5) {
          target.classList.add('active');
          
          if (activeModal) return;
          
          // Only autoplay if item has been loaded
          if (!loadedItemsRef.current.has(index)) return;
          
          const videoEl = target.querySelector('[data-feed-video="true"]');
          const videoState = videoStatesRef.current[index] || {};
          
          if (videoEl && videoEl.paused && !videoState.userPaused) {
            videoEl.muted = false;
            videoEl.setAttribute('playsinline', 'true');
            
            // Only play if already loaded
            if (videoEl.readyState >= 2) {
              videoEl.play()
                .then(() => {
                  updateVideoState(index, { playing: true, userPaused: false });
                })
                .catch((err) => {
                  logger.logError(err, 'SocialFeed initial video autoplay blocked');
                });
            } else {
              // Wait for it to load
              const playVideo = () => {
                videoEl.play()
                  .then(() => {
                    updateVideoState(index, { playing: true, userPaused: false });
                  })
                  .catch((err) => {
                    logger.logError(err, 'SocialFeed initial video autoplay blocked');
                  });
              };
              videoEl.addEventListener('loadeddata', playVideo, { once: true });
            }
          }
          
          if (item?.primaryMediaType === 'audio') {
            const audioEl = target.querySelector('audio');
            if (audioEl && audioEl.paused) {
              playAudioAtIndex(index);
            }
          }
        }
      });
    };
    
    // Run initial check after a short delay
    const initialCheckTimer = setTimeout(() => {
      checkInitialVisibleVideos();
    }, 100);

    return () => {
      clearTimeout(initialCheckTimer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('scroll', scrollHandler);
      clearTimeout(scrollTimeout);
    };
  }, [feedItems, normalizedSearch, playAudioAtIndex, updateVideoState, user, activeFilter, activeModal, audioStates]);

  useEffect(() => {
    if (!feedItems.length) return;

    let cancelled = false;

    const loadEngagement = async () => {
      const uniqueIds = [...new Set(feedItems.map((item) => item.contentId).filter(Boolean))];
      try {
        const results = await Promise.all(
          uniqueIds.map(async (contentId) => {
            try {
              const response = await socialEngagementService.getEngagement(contentId);
              return [
                contentId,
                {
                  likes: response.likes || 0,
                  likedBy: Array.isArray(response.likedBy) ? response.likedBy : [],
                  comments: Array.isArray(response.comments) ? response.comments : []
                }
              ];
            } catch (err) {
              logger.logError(err, 'SocialFeed load engagement entry');
              return [contentId, getDefaultEngagement()];
            }
          })
        );

        if (!cancelled) {
          setEngagementMap((prev) => {
            const next = { ...prev };
            results.forEach(([id, engagement]) => {
              next[id] = engagement;
            });
            return next;
          });

          if (user) {
            const nextLiked = {};
            results.forEach(([id, engagement]) => {
              if (engagement.likedBy?.includes(user.id)) {
                nextLiked[id] = true;
              }
            });
            setLikedContent(nextLiked);
          }
        }
      } catch (err) {
        logger.logError(err, 'SocialFeed load engagement bulk');
      }
    };

    loadEngagement();

    return () => {
      cancelled = true;
    };
  }, [feedItems, user]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!activeModal) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeModal]);

  // Cleanup: Pause all videos and audio when component unmounts or user navigates away
  useEffect(() => {
    const sections = sectionsRef.current;
    const modalVideo = modalVideoRef.current;
    const modalAudio = modalAudioRef.current;
    
    return () => {
      // Pause all feed videos
      sections.forEach((section) => {
        if (!section) return;
        const videoEl = section.querySelector('[data-feed-video="true"]');
        const audioEl = section.querySelector('audio');
        if (videoEl && !videoEl.paused) {
          videoEl.pause();
        }
        if (audioEl && !audioEl.paused) {
          audioEl.pause();
        }
      });
      
      // Pause modal video/audio if open
      if (modalVideo && !modalVideo.paused) {
        modalVideo.pause();
      }
      if (modalAudio && !modalAudio.paused) {
        modalAudio.pause();
      }
    };
  }, []);

  // Auto-play video when music video modal opens
  useEffect(() => {
    if (!activeModal) return;
    
    const modalItem = activeModal?.item || null;
    const modalContentType = modalItem ? detectContentType(modalItem) : null;
    const isMusic = (modalContentType || '').toLowerCase().includes('music');
    const isMusicVideo = isMusic && modalItem?.primaryMediaType === 'video';
    
    if (!isMusicVideo) return;
    
    const video = modalVideoRef.current;
    if (!video) return;

    const audioSrc = modalItem?.audio?.[0] || null;

    // Auto-play video when modal opens
    const playVideo = async () => {
      try {
        video.currentTime = 0;
        await video.play();
                        // Video playing
      } catch (err) {
        logger.logError(err, 'Modal video autoplay failed');
      }
    };

    // Small delay to ensure video element is ready
    const timer = setTimeout(() => {
      playVideo();
    }, 100);

    // Handle video end - transition to audio
    const handleVideoEnd = () => {
      // Video paused
      const audio = modalAudioRef.current;
      if (audio && audioSrc) {
        audio.currentTime = 0;
        audio.play()
          .then(() => {
                        // Audio playing
          })
          .catch((err) => {
            logger.logError(err, 'Modal audio autoplay after video failed');
          });
      }
    };

    video.addEventListener('ended', handleVideoEnd);

    return () => {
      clearTimeout(timer);
      video.removeEventListener('ended', handleVideoEnd);
    };
  }, [activeModal]);

  const handleAudioToggle = useCallback(
    (index) => {
      const shouldPlay = !audioStates[index];
      if (shouldPlay) {
        playAudioAtIndex(index);
        analyticsService.trackEvent('social_feed_audio_play', {
          contentId: feedItems[index]?.rawContent?.id,
        });
      } else {
        pauseAudioAtIndex(index);
        analyticsService.trackEvent('social_feed_audio_pause', {
          contentId: feedItems[index]?.rawContent?.id,
        });
      }
    },
    [audioStates, feedItems, pauseAudioAtIndex, playAudioAtIndex]
  );

  const handleLikeToggle = useCallback(
    async (item) => {
      const contentId = item.contentId;
      if (!contentId) return;

      if (!user) {
        navigate('/auth');
        return;
      }

      const entry = engagementMap[contentId] || getDefaultEngagement();
      const likedBy = Array.isArray(entry.likedBy) ? [...entry.likedBy] : [];
      const currentlyLiked = likedBy.includes(user.id);
      const nextLiked = !currentlyLiked;

      if (nextLiked) {
        if (!likedBy.includes(user.id)) {
          likedBy.push(user.id);
        }
      } else {
        const index = likedBy.indexOf(user.id);
        if (index !== -1) {
          likedBy.splice(index, 1);
        }
      }

      setEngagementMap((prev) => ({
        ...prev,
        [contentId]: {
          ...entry,
          likedBy,
          likes: likedBy.length
        }
      }));

      setLikedContent((prev) => {
        if (nextLiked) {
          return { ...prev, [contentId]: true };
        }
        const { [contentId]: _omit, ...rest } = prev;
        return rest;
      });

      try {
        const response = await socialEngagementService.likeContent(contentId, nextLiked, user);
        setEngagementMap((prev) => {
          const current = prev[contentId] || getDefaultEngagement();
          const serverLikedBy = Array.isArray(response.likedBy) ? response.likedBy : current.likedBy || [];
          return {
            ...prev,
            [contentId]: {
              ...current,
              likedBy: serverLikedBy,
              likes: response.likes ?? serverLikedBy.length
            }
          };
        });
        setLikedContent((prev) => {
          if (nextLiked) {
            return { ...prev, [contentId]: true };
          }
          const { [contentId]: _omit, ...rest } = prev;
          return rest;
        });
      } catch (err) {
        logger.logError(err, 'SocialFeed like toggle');
        setEngagementMap((prev) => ({
          ...prev,
          [contentId]: entry
        }));
        setLikedContent((prev) => {
          if (currentlyLiked) {
            return { ...prev, [contentId]: true };
          }
          const { [contentId]: _omit, ...rest } = prev;
          return rest;
        });
      }
    },
    [engagementMap, navigate, user]
  );

  const openModal = (item, index) => {
    if (!item) return;
    // Pause all feed videos and audio when opening modal to prevent background playback
    pauseAllFeedVideos();
    pauseAudioAtIndex(index);
    setActiveModal({ item, index });
    const displayName = user
      ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Zee Fan'
      : defaultDisplayName || '';
    setCommentForm({
      name: displayName,
      message: ''
    });
    setCommentError('');
  };

  const closeModal = useCallback(() => {
    // Pause video and audio when closing modal
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
      modalVideoRef.current.currentTime = 0;
    }
    if (modalAudioRef.current) {
      modalAudioRef.current.pause();
      modalAudioRef.current.currentTime = 0;
    }
    setActiveModal(null);
    setCommentError('');
    
    // Resume feed videos that were playing before modal opened
    // Only resume if they were already playing (user had clicked play)
    sectionsRef.current.forEach((section, index) => {
      if (!section) return;
      
      // Check if section is in view (has 'active' class)
      if (!section.classList.contains('active')) return;
      
      const videoEl = section.querySelector('[data-feed-video="true"]');
      if (!videoEl) return;
      
      const videoState = videoStatesRef.current[index] || {};
      
      // Only resume if video was playing before and user didn't pause it
      if (videoState.playing && !videoState.userPaused && videoEl.paused) {
        const isMusicVideo = videoEl.dataset.musicVideo === 'true';
        if (isMusicVideo) {
          videoEl.muted = false;
        } else {
          videoEl.muted = true;
        }
        videoEl.play()
          .then(() => {
            updateVideoState(index, { playing: true, userPaused: false });
          })
          .catch(() => {
            // Autoplay blocked, that's fine
          });
      }
    });
  }, [updateVideoState]);

  const handleCommentSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!activeModal?.item?.contentId) return;

      if (!user) {
        navigate('/auth');
        return;
      }

      const contentId = activeModal.item.contentId;
      const name = (commentForm.name || '').trim();
      const message = (commentForm.message || '').trim();

      if (!message) {
        setCommentError('Share a quick note before sending it to Zee.');
        return;
      }

      setCommentSubmitting(true);
      setCommentError('');

      try {
        const response = await socialEngagementService.addComment(
          contentId,
          { name, message },
          user
        );
        setEngagementMap((prev) => {
          const entry = prev[contentId] || getDefaultEngagement();
          return {
            ...prev,
            [contentId]: {
              ...entry,
              likedBy: Array.isArray(response.likedBy) ? response.likedBy : entry.likedBy,
              likes: response.likedBy ? response.likedBy.length : entry.likes,
              comments: Array.isArray(response.comments) ? response.comments : entry.comments
            }
          };
        });

        const resolvedName = name || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Zee Fan';
        if (resolvedName && typeof window !== 'undefined') {
          window.localStorage.setItem(LOCAL_STORAGE_NAME_KEY, resolvedName);
          setDefaultDisplayName(resolvedName);
        }

        setCommentForm({
          name: resolvedName,
          message: ''
        });
      } catch (err) {
        logger.logError(err, 'SocialFeed add comment');
        setCommentError(err.message || 'Could not add your comment. Try again?');
      } finally {
        setCommentSubmitting(false);
      }
    },
    [activeModal, commentForm, navigate, user]
  );

  const modalItem = activeModal?.item || null;
  const modalContentId = modalItem?.contentId;
  const modalEngagement = modalContentId ? engagementMap[modalContentId] || getDefaultEngagement() : getDefaultEngagement();
  const modalContentType = modalItem ? detectContentType(modalItem) : null;
  const modalMusicLinks = modalItem?.streamingLinks || [];
  const modalProductLinks = modalItem?.productLinks || [];
  const modalIsMusic = (modalContentType || '').toLowerCase().includes('music');
  const modalIsMusicVideo = modalIsMusic && modalItem?.primaryMediaType === 'video';
  const modalAudioSrc = modalItem?.audio?.[0] || null;
  const modalImage = modalItem?.coverImage || modalItem?.images?.[0] || null;

  const feedBackground = useMemo(
    () =>
      'radial-gradient(circle at top, rgba(59, 130, 246, 0.2), transparent 50%), ' +
      'radial-gradient(circle at bottom, rgba(236, 72, 153, 0.15), transparent 55%), ' +
      '#050816',
    []
  );

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#050816',
          color: 'white',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(255, 255, 255, 0.15)',
            borderTopColor: '#38bdf8',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ fontSize: '1.1rem', letterSpacing: '0.02em' }}>Zee is tuning the vibe...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#050816',
          color: '#fca5a5',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '2rem', marginBottom: '12px', color: '#f87171' }}>Zee needs a moment</h1>
          <p style={{ maxWidth: '480px', margin: '0 auto', lineHeight: 1.5 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: feedBackground,
        color: 'white',
        paddingTop: '96px',
        paddingBottom: '48px',
      }}
    >
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(5, 8, 22, 0.85)',
          backdropFilter: 'blur(18px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <span style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.3em', color: '#38bdf8' }}>
          Zee Broadcasts
        </span>
        <h1
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            margin: 0,
            background: 'linear-gradient(135deg, #38bdf8, #a855f7, #f97316)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Zee&apos;s Social Stream
        </h1>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.95rem' }}>
          Scroll through Zee&apos;s living gallery where every swipe unlocks another pulse of colour, sound, and motion.
        </p>
      </header>

      <div
        style={{
          maxWidth: '720px',
          margin: '24px auto 0',
          padding: '0 16px',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        {filterOptions.map((option) => {
          const isActive = activeFilter === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setActiveFilter(option)}
              style={{
                padding: '8px 16px',
                borderRadius: '999px',
                border: isActive ? '1px solid rgba(56, 189, 248, 0.8)' : '1px solid rgba(148, 163, 184, 0.3)',
                background: isActive ? 'rgba(56, 189, 248, 0.18)' : 'rgba(15, 23, 42, 0.6)',
                color: isActive ? '#bae6fd' : '#e2e8f0',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
              }}
            >
              {formatFilterLabel(option)}
            </button>
          );
        })}
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search content..."
          style={{
            flex: '1 1 200px',
            minWidth: '200px',
            padding: '8px 12px',
            borderRadius: '12px',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            background: 'rgba(15, 23, 42, 0.6)',
            color: '#e2e8f0',
            fontSize: '0.9rem',
          }}
        />
      </div>

      <div
        style={{
          maxWidth: '720px',
          margin: '10px auto 0',
          padding: '8px 12px',
          borderRadius: '10px',
          border: '1px dashed rgba(56, 189, 248, 0.45)',
          background: 'rgba(2, 6, 23, 0.7)',
          color: '#cbd5e1',
          fontSize: '11px',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
        }}
      >
        {`debug fetched=${debugSnapshot?.fetchedCount ?? 'n/a'} built=${debugSnapshot?.builtCount ?? 'n/a'} sorted=${debugSnapshot?.sortedCount ?? 'n/a'} first=${debugSnapshot?.firstItem?.title ?? 'none'} type=${debugSnapshot?.firstItem?.type ?? 'n/a'} media=${debugSnapshot?.firstItem?.mediaType ?? 'n/a'}`}
      </div>

      <main
        style={{
          maxWidth: '720px',
          margin: '0 auto',
          padding: '24px 16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '48px',
        }}
      >
        {feedItems.map((item, index) => {
          const isAudioPlaying = !!audioStates[index];
          const contentId = item.contentId;
          const engagement = engagementMap[contentId] || getDefaultEngagement();
          const likeCount = engagement.likes || 0;
          const commentCount = engagement.comments?.length || 0;
          const isLiked = user ? engagement.likedBy?.includes(user.id) : false;
          const contentType = detectContentType(item);
          const contentTypeLower = (contentType || '').toLowerCase();
          const matchesFilter =
            activeFilter === 'all' ||
            contentTypeLower === activeFilter ||
            contentTypeLower.includes(activeFilter);
          const isMusicCard = contentTypeLower.includes('music') || item.primaryMediaType === 'audio';
          const cardMusicLinks = isMusicCard ? item.streamingLinks || [] : [];
          const cardProductLinks = item.productLinks || [];
          const postedDestinations = item.postedDestinations || [];
          const matchesSearch =
            !normalizedSearch ||
            (item.title && item.title.toLowerCase().includes(normalizedSearch)) ||
            (item.description && item.description.toLowerCase().includes(normalizedSearch)) ||
            contentTypeLower.includes(normalizedSearch) ||
            (Array.isArray(item.rawContent?.tags) &&
              item.rawContent.tags.some((tag) => typeof tag === 'string' && tag.toLowerCase().includes(normalizedSearch)));

          if (!matchesFilter || !matchesSearch) {
            sectionsRef.current[index] = null;
            return null;
          }

          // Use detected content type for display, not primaryMediaType
          const displayTypeLabel = formatFilterLabel(contentTypeLower || 'image');
          let typeLabelText = 'Image';
          let typeIconEl = <ImageIcon size={18} />;
          if (contentTypeLower.includes('music')) {
            typeLabelText = 'Audio';
            typeIconEl = <Music2 size={18} />;
          } else if (contentTypeLower.includes('video')) {
            typeLabelText = 'Video';
            typeIconEl = <VideoIcon size={18} />;
          }

          const primaryImage = item.coverImage || item.images?.[0] || null;
          const videoSrc = item.primaryMediaType === 'video' ? item.primaryMediaUrl : item.videos?.[0] || null;
          const audioSrc = item.primaryMediaType === 'audio' ? item.primaryMediaUrl : item.audio?.[0] || null;
          const videoState = videoStates[index] || {};
          const isVideoPlaying = !!videoState.playing;
          const videoMeta = videoMetadata[index];
          const aspectRatioValue =
            videoMeta && videoMeta.width && videoMeta.height
              ? `${videoMeta.width} / ${videoMeta.height}`
              : '9 / 16';
          
          // Check if this is a music video (music content with video)
          const isMusicVideo = isMusicCard && item.primaryMediaType === 'video';

          return (
            <article
              key={item.id}
              ref={(el) => registerSection(el, index)}
              data-index={index}
              style={{
                position: 'relative',
                borderRadius: '24px',
                overflow: 'hidden',
                background: 'rgba(15, 23, 42, 0.85)',
                border: '1px solid rgba(148, 163, 184, 0.1)',
                boxShadow: '0 24px 60px rgba(59, 130, 246, 0.15)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(135deg, rgba(56, 189, 248, 0.08), transparent 55%), linear-gradient(225deg, rgba(168, 85, 247, 0.08), transparent 55%)',
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  position: 'relative',
                  padding: '20px 20px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #a855f7, #38bdf8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    flexShrink: 0,
                  }}
                >
                  RS
                </div>
                <div style={{ flex: 1 }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: 'white',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {item.title || 'Untitled post'}
                  </h2>
                  <p style={{ margin: '4px 0', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#38bdf8', textTransform: 'uppercase' }}>
                    {displayTypeLabel}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#cbd5f5' }}>
                    {item.description || 'A fresh drop from the Rainbow Sails cosmos.'}
                  </p>
                  {postedDestinations.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {postedDestinations.map((dest, idx) => (
                        dest.url ? (
                          <a
                            key={`${dest.platform}-${idx}`}
                            href={dest.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.72rem',
                              color: '#a5f3fc',
                              border: '1px solid rgba(56, 189, 248, 0.4)',
                              borderRadius: 999,
                              padding: '2px 8px',
                              textDecoration: 'none',
                            }}
                          >
                            posted: {dest.platform}
                          </a>
                        ) : (
                          <span
                            key={`${dest.platform}-${idx}`}
                            style={{
                              fontSize: '0.72rem',
                              color: '#a5f3fc',
                              border: '1px solid rgba(56, 189, 248, 0.4)',
                              borderRadius: 999,
                              padding: '2px 8px',
                            }}
                          >
                            posted: {dest.platform}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#38bdf8',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {typeIconEl}
                  <span>{typeLabelText}</span>
                </div>
              </div>

              <div
                style={{
                  position: 'relative',
                  marginTop: '20px',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  marginLeft: '16px',
                  marginRight: '16px',
                  background: 'rgba(15, 23, 42, 0.75)',
                }}
              >
                {item.primaryMediaType === 'video' && videoSrc && (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: aspectRatioValue,
                      maxHeight: '85vh',
                      margin: '0 auto',
                      background: '#000',
                      overflow: 'hidden',
                      borderRadius: 'inherit',
                    }}
                  >
                    <video
                      data-feed-video="true"
                      data-music-video={isMusicVideo ? 'true' : undefined}
                      src={videoSrc}
                      muted={false}
                      loop={!isMusicVideo}
                      playsInline
                      preload="auto"
                      onLoadedMetadata={(event) => handleVideoMetadata(index, event)}
                      onClick={(event) => {
                        event.stopPropagation();
                        // For music videos, pause video and open modal with audio player and image
                        if (isMusicVideo) {
                          const videoEl = event.target;
                          if (videoEl && !videoEl.paused) {
                            videoEl.pause();
                            updateVideoState(index, { playing: false, userPaused: true });
                          }
                          openModal(item, index);
                        } else {
                        handleVideoToggle(index);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        backgroundColor: 'black',
                        cursor: 'pointer',
                      }}
                    />
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleVideoToggle(index);
                      }}
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        border: '1px solid rgba(148, 163, 184, 0.4)',
                        background: 'rgba(5, 8, 22, 0.6)',
                        color: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backdropFilter: 'blur(6px)',
                        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.45)',
                        zIndex: 2,
                      }}
                      aria-label={isVideoPlaying ? 'Pause Zee video' : 'Play Zee video'}
                    >
                      {isVideoPlaying ? <Pause size={22} /> : <Play size={22} />}
                    </button>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        right: '16px',
                        padding: '6px 12px',
                        borderRadius: '999px',
                        background: 'rgba(5, 8, 22, 0.55)',
                        color: '#e2e8f0',
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        pointerEvents: 'none',
                      }}
                    >
                      © Zee • Rainbow Sails
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '16px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: 'rgba(5, 8, 22, 0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <img
                        src={NAV_ICON_SRC}
                        alt="Rainbow Sails"
                        style={{ width: '24px', height: '24px', filter: 'drop-shadow(0 0 6px rgba(14, 165, 233, 0.6))' }}
                      />
                    </div>
                  </div>
                )}

                {item.primaryMediaType === 'image' && primaryImage && (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxHeight: '85vh',
                      background: '#000',
                      borderRadius: 'inherit',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={primaryImage}
                      alt={item.title || 'Rainbow Sails content'}
                      onError={handleImageFallback}
                      loading="lazy"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{
                        width: '100%',
                        height: '100%',
                        maxHeight: '85vh',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        right: '16px',
                        padding: '6px 12px',
                        borderRadius: '999px',
                        background: 'rgba(5, 8, 22, 0.55)',
                        color: '#e2e8f0',
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        pointerEvents: 'none',
                      }}
                    >
                      © Zee • Rainbow Sails
                    </div>
                  </div>
                )}

                {item.primaryMediaType === 'audio' && (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '32px 24px 24px',
                      gap: '16px',
                    }}
                  >
                    <div
                      style={{
                        width: '220px',
                        height: '220px',
                        borderRadius: '32px',
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: '0 24px 60px rgba(56, 189, 248, 0.25)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                      }}
                    >
                      {primaryImage ? (
                        <img
                          src={primaryImage}
                          alt="Zee audio cover"
                          onError={handleImageFallback}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', userSelect: 'none' }}
                          loading="lazy"
                          draggable={false}
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(135deg, #38bdf8, #a855f7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '3rem',
                          }}
                        >
                          🎧
                        </div>
                      )}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '12px',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: 'rgba(5, 8, 22, 0.6)',
                          color: '#e2e8f0',
                          fontSize: '0.65rem',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          pointerEvents: 'none',
                        }}
                      >
                        © Zee • Rainbow Sails
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '12px',
                          width: '34px',
                          height: '34px',
                          borderRadius: '10px',
                          background: 'rgba(5, 8, 22, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <img src={NAV_ICON_SRC} alt="Rainbow Sails" style={{ width: '20px', height: '20px' }} />
                      </div>
                      <button
                        onClick={() => handleAudioToggle(index)}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '72px',
                          height: '72px',
                          borderRadius: '50%',
                          border: 'none',
                          background: 'linear-gradient(135deg, #f97316, #fb7185)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 16px 30px rgba(249, 115, 22, 0.35)',
                        }}
                        aria-label={isAudioPlaying ? 'Pause Zee audio' : 'Play Zee audio'}
                      >
                        {isAudioPlaying ? <Pause size={30} /> : <Play size={30} />}
                      </button>
                    </div>
                    {audioSrc && <audio src={audioSrc} preload="auto" loop />}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        color: '#cbd5f5',
                        fontSize: '0.95rem',
                      }}
                    >
                      {isAudioPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
                      <span>{isAudioPlaying ? 'Zee is playing for you' : 'Tap to feel Zee&apos;s beat'}</span>
                    </div>
                  </div>
                )}

              </div>

              {isMusicCard && cardMusicLinks.length > 0 && (
                <div
                  style={{
                    padding: '12px 20px 0',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}
                >
                  {cardMusicLinks.map(({ label, url }) => (
                    <a
                      key={`${contentId}-${label}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '6px 12px',
                        borderRadius: '999px',
                        border: '1px solid rgba(56, 189, 248, 0.35)',
                        background: 'rgba(56, 189, 248, 0.12)',
                        color: '#bae6fd',
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}

              {cardProductLinks.length > 0 && (
                <div
                  style={{
                    padding: '12px 20px 0',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}
                >
                  {cardProductLinks.map(({ label, url }) => (
                    <a
                      key={`${contentId}-product-${label}`}
                      href={url}
                      target={url.startsWith('http') ? '_blank' : undefined}
                      rel={url.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '999px',
                        border: '1px solid rgba(22, 163, 74, 0.35)',
                        background: 'rgba(22, 163, 74, 0.12)',
                        color: '#bbf7d0',
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}

              <div
                style={{
                  padding: '16px 20px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!user) {
                        navigate('/auth');
                        return;
                      }
                      handleLikeToggle(item);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '999px',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      background: isLiked ? 'rgba(236, 72, 153, 0.18)' : 'transparent',
                      color: isLiked ? '#f472b6' : '#e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: 600,
                    }}
                  >
                    <Heart size={18} fill={isLiked ? '#f472b6' : 'none'} />
                    <span style={{ fontSize: '0.85rem' }}>{likeCount}</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(item, index);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '999px',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      background: 'transparent',
                      color: '#e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: 600,
                    }}
                  >
                    <MessageCircle size={18} />
                    <span style={{ fontSize: '0.85rem' }}>{commentCount}</span>
                  </button>

                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(item, index);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(56, 189, 248, 0.4)',
                    background: 'rgba(56, 189, 248, 0.18)',
                    color: '#bae6fd',
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ExternalLink size={18} />
                  <span style={{ fontSize: '0.85rem' }}>View Details</span>
                </button>
              </div>
            </article>
          );
        })}
      </main>
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(5, 8, 22, 0.92)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '960px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'rgba(15, 23, 42, 0.95)',
              borderRadius: '24px',
              padding: '32px',
              position: 'relative',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              boxShadow: '0 24px 60px rgba(56, 189, 248, 0.25)',
            }}
          >
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: '1px solid rgba(148, 163, 184, 0.4)',
                background: 'rgba(15, 23, 42, 0.9)',
                color: '#e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <header style={{ marginBottom: '24px' }}>
              <span style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.25em', color: '#38bdf8' }}>
                Zee Spotlight
              </span>
              <h2
                style={{
                  margin: '8px 0 12px 0',
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: 'white',
                }}
              >
                {modalItem.title || 'Zee creation'}
              </h2>
              {modalContentType && (
                <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#bae6fd' }}>
                  {formatFilterLabel(modalContentType.toLowerCase())}
                </p>
              )}
              <p style={{ color: '#cbd5f5', fontSize: '1rem', lineHeight: 1.5 }}>
                {modalItem.description || 'Zee left this one shimmering for you.'}
              </p>
            </header>

            <section
              style={{
                borderRadius: '20px',
                overflow: 'hidden',
                background: 'rgba(9, 13, 30, 0.85)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                marginBottom: '24px',
              }}
            >
              {modalItem.primaryMediaType === 'image' && (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '70vh',
                    maxHeight: '70vh',
                    backgroundImage: `url(${modalItem.coverImage || modalItem.images?.[0] || ''})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundColor: '#000',
                    userSelect: 'none'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255, 255, 255, 0.1)',
                      pointerEvents: 'none'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '24px',
                      padding: '8px 14px',
                      borderRadius: '999px',
                      background: 'rgba(5, 8, 22, 0.65)',
                      color: '#e2e8f0',
                      fontSize: '0.8rem',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      pointerEvents: 'none',
                    }}
                  >
                    © Zee • Rainbow Sails
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      left: '24px',
                      width: '48px',
                      height: '48px',
                      borderRadius: '14px',
                      background: 'rgba(5, 8, 22, 0.65)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <img src={NAV_ICON_SRC} alt="Rainbow Sails" style={{ width: '26px', height: '26px' }} />
                  </div>
                </div>
              )}

              {modalIsMusicVideo && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '32px',
                    gap: '24px',
                  }}
                >
                  {/* MP4 Video - plays first, once, no loop */}
                  {modalItem.primaryMediaUrl && (
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        position: 'relative',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        background: '#000',
                        border: '1px solid rgba(148, 163, 184, 0.12)',
                      }}
                    >
                      <video
                        ref={modalVideoRef}
                        src={modalItem.primaryMediaUrl}
                        controls
                        loop={false}
                        playsInline
                        preload="auto"
                        style={{
                          width: '100%',
                          display: 'block',
                          maxHeight: '70vh',
                        }}
                        onPlay={() => {}}
                        onPause={() => {}}
                      />
                      {/* Control buttons overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '16px',
                          right: '16px',
                          display: 'flex',
                          gap: '8px',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (modalVideoRef.current) {
                              modalVideoRef.current.pause();
                              // Video paused
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '1px solid rgba(148, 163, 184, 0.3)',
                            background: 'rgba(15, 23, 42, 0.9)',
                            color: '#e2e8f0',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <Pause size={16} />
                          Pause Video
                        </button>
                        {modalAudioSrc && (
                          <button
                            type="button"
                            onClick={() => {
                              if (modalVideoRef.current) {
                                modalVideoRef.current.pause();
                                // Video paused
                              }
                              if (modalAudioRef.current) {
                                modalAudioRef.current.currentTime = 0;
                                modalAudioRef.current.play()
                                  .then(() => {})
                                  .catch((err) => logger.logError(err, 'Manual audio play failed'));
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: '1px solid rgba(56, 189, 248, 0.4)',
                              background: 'rgba(56, 189, 248, 0.18)',
                              color: '#bae6fd',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <Play size={16} />
                            Play Audio
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Audio Player - shows below video */}
                  {modalAudioSrc && (
                    <div
                      style={{
                        width: '100%',
                        padding: '20px',
                        borderRadius: '18px',
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(148, 163, 184, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#cbd5f5', marginBottom: '8px' }}>
                        <Headphones size={20} />
                        <span style={{ fontWeight: 600, letterSpacing: '0.05em' }}>Listen to the full track</span>
                      </div>
                      <audio
                        ref={modalAudioRef}
                        controls
                        preload="auto"
                        src={modalAudioSrc}
                        style={{
                          width: '100%',
                          outline: 'none',
                          filter: 'drop-shadow(0 6px 14px rgba(56, 189, 248, 0.25))',
                        }}
                        onPlay={() => {}}
                        onPause={() => {}}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                  
                  {/* Image below audio player */}
                  {modalImage && (
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        border: '1px solid rgba(148, 163, 184, 0.12)',
                      }}
                    >
                      <img
                        src={modalImage}
                        alt={modalItem.title ? `${modalItem.title} cover art` : 'Zee cover art'}
                        style={{
                          width: '100%',
                          display: 'block',
                          objectFit: 'cover',
                          userSelect: 'none',
                        }}
                        loading="lazy"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                  )}
                </div>
              )}

              {modalItem.primaryMediaType === 'video' && !modalIsMusicVideo && modalItem.primaryMediaUrl && (
                <video
                  src={modalItem.primaryMediaUrl}
                  controls
                  style={{ width: '100%', maxHeight: '70vh', background: '#000' }}
                />
              )}

              {modalItem.primaryMediaType === 'audio' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '32px',
                    gap: '20px',
                  }}
                >
                  <div
                    style={{
                      width: '240px',
                      height: '240px',
                      borderRadius: '32px',
                      overflow: 'hidden',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      boxShadow: '0 24px 60px rgba(56, 189, 248, 0.2)',
                    }}
                  >
                    {modalItem.coverImage ? (
                      <img
                        src={modalItem.coverImage}
                        alt="Zee audio cover"
                        onError={handleImageFallback}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #38bdf8, #a855f7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '3rem',
                        }}
                      >
                        🎧
                      </div>
                    )}
                  </div>
                  <audio src={modalItem.primaryMediaUrl} controls preload="auto" style={{ width: '100%' }} />
                </div>
              )}
            </section>

            {modalIsMusic && modalMusicLinks.length > 0 && (
              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#bae6fd', fontSize: '1.1rem', marginBottom: '12px' }}>Stream Zee&apos;s track</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {modalMusicLinks.map(({ label, url }) => (
                    <a
                      key={`${label}-${url}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        borderRadius: '999px',
                        border: '1px solid rgba(56, 189, 248, 0.4)',
                        background: 'rgba(56, 189, 248, 0.12)',
                        color: '#bae6fd',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      <ExternalLink size={16} />
                      <span>{label}</span>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {modalProductLinks.length > 0 && (
              <section style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#bbf7d0', fontSize: '1.1rem', marginBottom: '12px' }}>Own Zee&apos;s creation</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {modalProductLinks.map(({ label, url }) => (
                    <a
                      key={`modal-product-${label}-${url}`}
                      href={url}
                      target={url.startsWith('http') ? '_blank' : undefined}
                      rel={url.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        borderRadius: '999px',
                        border: '1px solid rgba(34, 197, 94, 0.4)',
                        background: 'rgba(34, 197, 94, 0.12)',
                        color: '#bbf7d0',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      <ExternalLink size={16} />
                      <span>{label}</span>
                    </a>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3 style={{ color: '#bae6fd', fontSize: '1.1rem', marginBottom: '12px' }}>Zee&apos;s community</h3>

              <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {modalEngagement.comments && modalEngagement.comments.length > 0 ? (
                  modalEngagement.comments.slice(0, 10).map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: 'rgba(9, 13, 30, 0.8)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#e2e8f0' }}>
                        <span style={{ fontWeight: 600 }}>{comment.name}</span>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {new Date(comment.createdAt).toLocaleString('en-AU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#cbd5f5', fontSize: '0.95rem', lineHeight: 1.5 }}>{comment.message}</p>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: 'rgba(9, 13, 30, 0.7)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      color: '#94a3b8',
                      textAlign: 'center',
                    }}
                  >
                    Be the first to leave a note for Zee.
                  </div>
                )}
              </div>

              {user ? (
                <>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {emojiOptions.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setCommentForm((prev) => ({ ...prev, message: `${prev.message}${emoji}` }))}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '999px',
                          border: '1px solid rgba(148, 163, 184, 0.3)',
                          background: 'rgba(9, 13, 30, 0.75)',
                          color: '#e2e8f0',
                          cursor: 'pointer',
                          fontSize: '1rem',
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>

                  <form onSubmit={handleCommentSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={commentForm.name}
                        onChange={(e) => setCommentForm((prev) => ({ ...prev, name: e.target.value }))}
                        style={{
                          flex: 1,
                          minWidth: '200px',
                          padding: '12px',
                          borderRadius: '10px',
                          border: '1px solid rgba(148, 163, 184, 0.3)',
                          background: 'rgba(9, 13, 30, 0.8)',
                          color: '#e2e8f0',
                        }}
                      />
                    </div>
                    <textarea
                      placeholder="Leave a message for Zee..."
                      value={commentForm.message}
                      onChange={(e) => setCommentForm((prev) => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        background: 'rgba(9, 13, 30, 0.8)',
                        color: '#e2e8f0',
                        resize: 'vertical',
                        minHeight: '100px',
                      }}
                      required
                    />
                    {commentError && (
                      <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{commentError}</div>
                    )}
                    <button
                      type="submit"
                      disabled={commentSubmitting}
                      style={{
                        alignSelf: 'flex-start',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 18px',
                        borderRadius: '12px',
                        border: '1px solid rgba(236, 72, 153, 0.5)',
                        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.28), rgba(168, 85, 247, 0.28))',
                        color: '#fbcfe8',
                        cursor: commentSubmitting ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        opacity: commentSubmitting ? 0.7 : 1,
                      }}
                    >
                      {commentSubmitting ? 'Sending...' : 'Send to Zee'}
                    </button>
                  </form>
                </>
              ) : (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    background: 'rgba(9, 13, 30, 0.75)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    color: '#e2e8f0',
                    textAlign: 'center',
                  }}
                >
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Sign in to share your vibes with Zee.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/auth')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '12px',
                      border: '1px solid rgba(236, 72, 153, 0.5)',
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.28), rgba(168, 85, 247, 0.28))',
                      color: '#fbcfe8',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Sign In
                  </button>
                </div>
              )}

            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialFeed;

